import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Button, CodeInput, Modal as UIModal } from "src/components/ui";
import { colors } from "src/theme/colors";
import { space } from "src/theme/ui";

/**
 * The one-time code, before an order, an exit or a password change goes
 * through.
 *
 * It was the last dialog in the app built its own way: an 819px sheet for a
 * single field, the title and both sentences centred, a tinted panel inside
 * the white surface holding a 300px text box with no length and no label,
 * and the actions in a custom footer of its own. Since the code box a client
 * meets right after registering became six cells, this was also the only
 * place left where the same question was asked with a different object.
 *
 * Now it is the shell every other dialog uses — title left in ink, the
 * reason under it, the actions in the foot band — with the shared code
 * field in the body. Its props are unchanged, so the six flows that open it
 * are untouched.
 */

/* How long the reader waits before a new code can be asked for. The service
   holds a code for four minutes; this is only the guard against hammering
   the button. */
const RESEND_AFTER = 30;

const Sent = styled.p`
  margin: 0 0 ${space.lg}px;
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.gray[600]};
`;

/* Under the field, where this form's errors go. */
const Rejected = styled.p`
  margin: ${space.md}px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${colors.error[600]};
`;

const OTPModal = ({ formik, isModalVisible, setModalVisible, onResend, error }) => {
  /**
   * The wait, COUNTED DOWN rather than merely served.
   *
   * The button was disabled for thirty seconds and said "Resend OTP" the
   * whole time, so a reader whose code had not arrived was left pressing a
   * dead control with nothing telling them why or for how long. It says how
   * many seconds are left, which is the one fact that makes waiting
   * bearable.
   */
  const [secondsLeft, setSecondsLeft] = useState(RESEND_AFTER);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((left) => left - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  /* The code arrives as digits, not as an event: the field holds one value
     for all six cells, and formik is told the value directly. */
  const code = formik.values?.otpCode ?? "";

  const resend = () => {
    /* A new code makes the one being typed wrong, so the row is cleared
       rather than left holding digits that will now be refused. */
    formik.setFieldValue("otpCode", "");
    setSecondsLeft(RESEND_AFTER);
    if (onResend) onResend();
  };

  return (
    <UIModal
      title="Enter your code"
      description="We sent a six digit code to your email and your phone."
      size="sm"
      visible={isModalVisible}
      onCancel={() => setModalVisible(false)}
      /* Straight into the shell's foot band, which is already the action
         row: secondary before primary, ends of the dialog. Wrapping them in
         a DialogActions of their own put a second action row inside the
         first and stood the buttons on top of each other. */
      footer={
        <>
          <Button
            variant="secondary"
            onClick={resend}
            disabled={secondsLeft > 0 || formik.isSubmitting}
          >
            {secondsLeft > 0 ? `Resend code in ${secondsLeft}s` : "Resend code"}
          </Button>
          <Button
            type="submit"
            onClick={formik.handleSubmit}
            loading={formik.isSubmitting}
            /* Six digits or nothing: the field cannot hold a seventh, and a
               short code is a round trip that can only fail. */
            disabled={code.length < 6}
          >
            Verify
          </Button>
        </>
      }
    >
      <Sent>
        This confirms it is you. The code expires shortly, so if it has not
        arrived, ask for a new one.
      </Sent>

      <CodeInput
        autoFocus
        /* The sentence above is this field's label, so its name is given to
           a screen reader rather than printed a second time over the row. */
        label="One-time code"
        name="otpCode"
        id="otpCode"
        value={code}
        invalid={Boolean(error)}
        onChange={(value) => formik.setFieldValue("otpCode", value)}
        onBlur={formik.handleBlur}
      />

      {error ? <Rejected>{error}</Rejected> : null}
    </UIModal>
  );
};

export default OTPModal;
